import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  makeStyles,
  Paper
} from '@material-ui/core';
import { 
  Event,
  Person,
  Schedule,
  Info,
  EventBusy,
  PlayCircleFilled,
  CheckCircle,
  Help,
  Videocam
} from '@material-ui/icons';
import { getMeetingStatus } from '../utils/meetingUtils';

const useStyles = makeStyles((theme) => ({
  tableCard: {
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  tableContainer: {
    borderRadius: 12,
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[50],
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
    },
  },
  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.grey[25],
    },
  },
  organizerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  meetingTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  meetingSubject: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  statusChip: {
    fontWeight: 500,
    borderRadius: 16,
    '& .MuiChip-label': {
      color: '#ffffff !important',
      fontWeight: '500 !important',
    },
    '& .MuiChip-icon': {
      color: '#ffffff !important',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  joinButton: {
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 500,
  },
}));

const MeetingsTable = ({ meetings, onMeetingClick }) => {
  const classes = useStyles();
  const [failedImages, setFailedImages] = useState(new Set());

  const handleImageError = (meetingId) => {
    setFailedImages(prev => new Set([...prev, meetingId]));
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusConfig = (status) => {
    const configs = {
      'in_progress': { 
        label: 'En Curso', 
        color: 'white',
        icon: PlayCircleFilled
      },
      'ended': { 
        label: 'Finalizada', 
        color: 'default',
        icon: CheckCircle
      },
      'not_started': { 
        label: 'No Iniciada', 
        color: 'secondary',
        icon: Schedule
      },
    };
    return configs[status] || { 
      label: 'Desconocido', 
      color: 'default',
      icon: Help
    };
  };

  const formatDate = (dateString, timeZone) => {
    let date;
    
    const hasTz = dateString.endsWith('Z') || dateString.match(/[-+]\d{2}:?\d{2}$/);
    
    if (!hasTz && timeZone === 'UTC') {
      const cleanDate = dateString.replace(/\.\d+$/, '') + 'Z';
      date = new Date(cleanDate);
    } else {
      date = new Date(dateString);
    }
    
    return {
      date: date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit'
      }),
    };
  };

  if (meetings.length === 0) {
    return (
      <Card className={classes.tableCard}>
        <CardContent>
          <Box className={classes.emptyState}>
            <EventBusy style={{ fontSize: 48, marginBottom: 16, color: 'inherit' }} />
            <Typography variant="h6" gutterBottom>
              No se encontraron reuniones
            </Typography>
            <Typography variant="body2">
              Ajusta los filtros para ver más resultados
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.tableCard}>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Event style={{ marginRight: 8 }} />
                  Reunión
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Person style={{ marginRight: 8 }} />
                  Organizador
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Schedule style={{ marginRight: 8 }} />
                  Fecha y Hora
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Info style={{ marginRight: 8 }} />
                  Estado
                </Box>
              </TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {meetings.map((meeting) => {
              const currentStatus = getMeetingStatus(
                meeting.scheduledStart, 
                meeting.scheduledEnd,
                meeting.timeZone
              );
              const statusConfig = getStatusConfig(currentStatus);
              const { date, time } = formatDate(meeting.scheduledStart, meeting.timeZone);

              return (
                <TableRow
                  key={meeting.id}
                  className={classes.tableRow}
                  onClick={() => onMeetingClick && onMeetingClick(meeting.id)}
                >
                  <TableCell>
                    <Box>
                      <Typography className={classes.meetingTitle}>
                        {meeting.title}
                      </Typography>
                      {meeting.subject && (
                        <Typography className={classes.meetingSubject}>
                          {meeting.subject}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box className={classes.organizerInfo}>
                      <Box position="relative">
                        {meeting.organizer.photoUrl && !failedImages.has(meeting.id) ? (
                          <Box
                            className="teams-avatar"
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={meeting.organizer.photoUrl}
                              alt={meeting.organizer.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={() => handleImageError(meeting.id)}
                            />
                          </Box>
                        ) : (
                          <Box
                            className="teams-avatar-fallback"
                            style={{
                              width: 40,
                              height: 40,
                              fontSize: '14px',
                            }}
                          >
                            {getInitials(meeting.organizer.name)}
                          </Box>
                        )}
                        <Box
                          className={`teams-status-indicator ${
                            meeting.organizer.hasJoined ? 'teams-status-online' : ''
                          }`}
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: meeting.organizer.hasJoined ? '#4caf50' : '#9e9e9e',
                            border: '2px solid white',
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                          }}
                          title={
                            meeting.organizer.hasJoined
                              ? 'El organizador se ha unido'
                              : 'El organizador no se ha unido'
                          }
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {meeting.organizer.name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          style={{
                            color: meeting.organizer.hasJoined
                              ? '#4caf50'
                              : currentStatus === 'in_progress'
                              ? '#ff9800'
                              : '#9e9e9e'
                          }}
                        >
                          {meeting.organizer.hasJoined
                            ? '✓ Unido'
                            : currentStatus === 'in_progress'
                            ? 'No unido'
                            : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {date}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {time}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={React.createElement(statusConfig.icon)}
                      label={statusConfig.label}
                      color={statusConfig.color}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Unirse a la reunión">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMeetingClick && onMeetingClick(meeting.id);
                        }}
                      >
                        <Videocam />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export { MeetingsTable };
export default MeetingsTable;